import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import SearchSelectBoxDrown from '../../components/common/SelectBox/searchSelect';
import authenticatedAxios from '../../authenticatedAxios';
import { baseurl } from '../../api';
import { toast } from 'react-toastify';
import './Accounting.css';

const ACCOUNT_FILTER_RULES = {
  exchange_gain_account: { category: ["Income"], types: ["Income", "Other Income"] },
  exchange_loss_account: { category: ["Expense"], types: ["Expenses", "Other Expenses"] },
  stock_valuation_account: { category: ["Assets"], types: ["Current Assets"] },
  stock_adjustment_account: { category: ["Expense"], types: ["Cost of Revenue", "Expenses"] },
  cogs_account: { category: ["Expense"], types: ["Cost of Revenue"] },
  grni_account: { category: ["Liabilities"], types: ["Current Liabilities"] },
  default_accounts_receivable: { category: ["Assets"], types: ["Receivable"] },
  default_accounts_payable: { category: ["Liabilities"], types: ["Payable"] },
  customer_advance_account: { category: ["Liabilities"], types: ["Current Liabilities"] },
  vendor_advance_account: { category: ["Assets"], types: ["Current Assets"] },
  tax_payable_account: { category: ["Liabilities"], types: ["Current Liabilities"] },
  tax_receivable_account: { category: ["Assets"], types: ["Current Assets"] },
  rounding_account: { category: ["Expense"], types: ["Expenses"] },
  suspense_account: { category: ["Assets"], types: ["Current Assets"] },
  retained_earnings_account: { category: ["Equity"], types: ["Equity"] },
  opening_balance_equity_account: { category: ["Equity"], types: ["Equity"] },
  discount_expense_account: { category: ["Expense"], types: ["Cost of Revenue", "Expenses"] },
  discount_income_account: { category: ["Income"], types: ["Income", "Other Income"] },
};

const JOURNAL_FILTER_RULES = {
  sales_journal: ["Sales"],
  purchase_journal: ["Purchase"],
  bank_journal: ["Bank"],
  cash_journal: ["Cash"],
  miscellaneous_journal: ["Miscellaneous"],
};

const AccountingConfigurationEdit = () => {
  const navigate = useNavigate();
  const [journals, setJournals] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [accountOptionsMap, setAccountOptionsMap] = useState({});
  const [rawConfigData, setRawConfigData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    sales_journal: '',
    purchase_journal: '',
    bank_journal: '',
    cash_journal: '',
    miscellaneous_journal: '',

    exchange_gain_account: '',
    exchange_loss_account: '',
    stock_valuation_account: '',
    stock_adjustment_account: '',
    cogs_account: '',
    default_accounts_receivable: '',
    default_accounts_payable: '',
    customer_advance_account: '',
    vendor_advance_account: '',
    tax_payable_account: '',
    tax_receivable_account: '',
    rounding_account: '',
    suspense_account: '',
    retained_earnings_account: '',
    opening_balance_equity_account: '',
    grni_account: '',
    discount_expense_account: '',
    discount_income_account: '',
  });

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchAccounts(),
          fetchJournalOptions(),
          fetchConfigDetail(),
        ]);
      } catch (error) {
        console.error('Failed to load configuration edit options or detail:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  const fetchAccounts = async () => {
    try {
      const ruleKeys = Object.keys(ACCOUNT_FILTER_RULES);
      const optionsMap = {};

      const [allAccountsRes] = await Promise.all([
        authenticatedAxios.get(`${baseurl}accounting/chartofaccount/active/`),
        ...ruleKeys.map(async (field) => {
          const rule = ACCOUNT_FILTER_RULES[field];
          const params = {};
          if (rule?.category?.length) params.category = rule.category.join(',');
          if (rule?.types?.length) params.account_types = rule.types.join(',');

          const response = await authenticatedAxios.get(`${baseurl}accounting/chartofaccount/active/`, { params });
          const accountsList = response?.data?.data || (Array.isArray(response?.data) ? response.data : []);
          optionsMap[field] = Array.isArray(accountsList) ? accountsList : [];
        }),
      ]);

      const allList = allAccountsRes?.data?.data || (Array.isArray(allAccountsRes?.data) ? allAccountsRes.data : []);
      setAllAccounts(Array.isArray(allList) ? allList : []);
      setAccountOptionsMap(optionsMap);
    } catch (error) {
      console.error('Error fetching Active Chart of Accounts:', error);
    }
  };

  const fetchJournalOptions = async () => {
    try {
      const journalRes = await authenticatedAxios.get(`${baseurl}accounting/master-journal/`);
      const journalList = journalRes?.data?.data || journalRes?.data?.results || (Array.isArray(journalRes?.data) ? journalRes.data : []);
      if (Array.isArray(journalList)) {
        setJournals(journalList);
      }
    } catch (error) {
      console.error('Failed to fetch master journals', error);
    }
  };

  const getVal = (val) => {
    if (!val) return '';
    return typeof val === 'object' ? val.id || '' : val;
  };

  const fetchConfigDetail = async () => {
    try {
      const configRes = await authenticatedAxios.get(`${baseurl}accounting/master-accounting-configuration/`);
      if (configRes?.data && configRes?.data?.data) {
        const c = configRes.data.data;
        setRawConfigData(c);
        setFormData({
          sales_journal: getVal(c.sales_journal),
          purchase_journal: getVal(c.purchase_journal),
          bank_journal: getVal(c.bank_journal),
          cash_journal: getVal(c.cash_journal),
          miscellaneous_journal: getVal(c.miscellaneous_journal),

          exchange_gain_account: getVal(c.exchange_gain_account),
          exchange_loss_account: getVal(c.exchange_loss_account),
          stock_valuation_account: getVal(c.stock_valuation_account),
          stock_adjustment_account: getVal(c.stock_adjustment_account),
          cogs_account: getVal(c.cogs_account),
          default_accounts_receivable: getVal(c.default_accounts_receivable),
          default_accounts_payable: getVal(c.default_accounts_payable),
          customer_advance_account: getVal(c.customer_advance_account),
          vendor_advance_account: getVal(c.vendor_advance_account),
          tax_payable_account: getVal(c.tax_payable_account),
          tax_receivable_account: getVal(c.tax_receivable_account),
          rounding_account: getVal(c.rounding_account),
          suspense_account: getVal(c.suspense_account),
          retained_earnings_account: getVal(c.retained_earnings_account),
          opening_balance_equity_account: getVal(c.opening_balance_equity_account),
          grni_account: getVal(c.grni_account),
          discount_expense_account: getVal(c.discount_expense_account),
          discount_income_account: getVal(c.discount_income_account),
        });
      }
    } catch (error) {
      console.error('Error fetching master configuration detail:', error);
    }
  };

  const getFilteredAccountOptions = (fieldName, selectedValue) => {
    const rawList = accountOptionsMap[fieldName] || [];
    let filtered = [...rawList];

    if (selectedValue) {
      const isSelectedInFiltered = filtered.some(
        (acc) => String(acc.id) === String(selectedValue)
      );
      if (!isSelectedInFiltered) {
        const found = allAccounts.find(
          (acc) => String(acc.id) === String(selectedValue)
        );
        if (found) {
          filtered = [found, ...filtered];
        } else {
          const fieldNameProp = `${fieldName}_name`;
          const accountName =
            rawConfigData[fieldNameProp] ||
            (typeof rawConfigData[fieldName] === 'object'
              ? rawConfigData[fieldName]?.account_name
              : null);
          const code =
            typeof rawConfigData[fieldName] === 'object'
              ? rawConfigData[fieldName]?.code
              : '';
          if (accountName) {
            filtered = [
              {
                id: selectedValue,
                account_name: accountName,
                code: code || '',
              },
              ...filtered,
            ];
          }
        }
      }
    }

    return filtered.map((acc) => ({
      label: acc.code ? `${acc.code} - ${acc.account_name}` : acc.account_name,
      value: acc.id,
    }));
  };

  const getFilteredJournalOptions = (fieldName, selectedValue) => {
    const allowedTypes = (JOURNAL_FILTER_RULES[fieldName] || []).map((t) =>
      t.toLowerCase().trim()
    );

    let filtered = journals.filter((j) => {
      if (allowedTypes.length === 0) return true;
      const jType = (
        j.journal_type_name ||
        j.journal_type?.name ||
        (typeof j.journal_type === 'string' ? j.journal_type : '')
      )
        .toString()
        .toLowerCase()
        .trim();
      return allowedTypes.includes(jType);
    });

    if (selectedValue) {
      const isSelectedInFiltered = filtered.some(
        (j) => String(j.id) === String(selectedValue)
      );
      if (!isSelectedInFiltered) {
        const selectedJ = journals.find(
          (j) => String(j.id) === String(selectedValue)
        );
        if (selectedJ) {
          filtered = [selectedJ, ...filtered];
        }
      }
    }

    return filtered.map((j) => ({
      label: j.name,
      value: j.id,
    }));
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value === '' ? null : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await authenticatedAxios.put(`${baseurl}accounting/master-accounting-configuration/`, formData);
      if (response?.data) {
        toast.success('Configuration saved successfully!');
        navigate('/accounting/configuration');
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      toast.error('Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="whole-container">
        <PageHeader pageName="Accounting / Configurations /" nestedpageName="Edit" linkTo="/accounting/configuration" />
        <div className="accounting-config-container">
          <p className="common-text fts-14 ftw-4">Loading form data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader pageName="Accounting / Configurations /" nestedpageName="Edit" linkTo="/accounting/configuration" />
      </div>

      <form onSubmit={handleSubmit} className="accounting-config-container">
        <div className="accounting-sub-header">
          Manage the default accounting configuration for your organization.
        </div>

        {/* DEFAULT JOURNALS */}
        <div className="accounting-card">
          <div className="accounting-card-title">DEFAULT JOURNALS</div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Default Sales Journal"
              options={getFilteredJournalOptions("sales_journal", formData.sales_journal)}
              defaultValue={formData.sales_journal}
              onChange={(e) => handleFieldChange("sales_journal", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Default Purchase Journal"
              options={getFilteredJournalOptions("purchase_journal", formData.purchase_journal)}
              defaultValue={formData.purchase_journal}
              onChange={(e) => handleFieldChange("purchase_journal", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Default Bank Journal"
              options={getFilteredJournalOptions("bank_journal", formData.bank_journal)}
              defaultValue={formData.bank_journal}
              onChange={(e) => handleFieldChange("bank_journal", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Default Cash Journal"
              options={getFilteredJournalOptions("cash_journal", formData.cash_journal)}
              defaultValue={formData.cash_journal}
              onChange={(e) => handleFieldChange("cash_journal", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Default Miscellaneous Journal"
              options={getFilteredJournalOptions("miscellaneous_journal", formData.miscellaneous_journal)}
              defaultValue={formData.miscellaneous_journal}
              onChange={(e) => handleFieldChange("miscellaneous_journal", e.target.value)}
            />
          </div>
        </div>

        {/* CURRENCY */}
        <div className="accounting-card">
          <div className="accounting-card-title">CURRENCY</div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Exchange Gain Account"
              options={getFilteredAccountOptions("exchange_gain_account", formData.exchange_gain_account)}
              defaultValue={formData.exchange_gain_account}
              onChange={(e) => handleFieldChange("exchange_gain_account", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Exchange Loss Account"
              options={getFilteredAccountOptions("exchange_loss_account", formData.exchange_loss_account)}
              defaultValue={formData.exchange_loss_account}
              onChange={(e) => handleFieldChange("exchange_loss_account", e.target.value)}
            />
          </div>
        </div>

        {/* INVENTORY */}
        <div className="accounting-card">
          <div className="accounting-card-title">INVENTORY</div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Stock Valuation Account"
              options={getFilteredAccountOptions("stock_valuation_account", formData.stock_valuation_account)}
              defaultValue={formData.stock_valuation_account}
              onChange={(e) => handleFieldChange("stock_valuation_account", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Stock Adjustment Account"
              options={getFilteredAccountOptions("stock_adjustment_account", formData.stock_adjustment_account)}
              defaultValue={formData.stock_adjustment_account}
              onChange={(e) => handleFieldChange("stock_adjustment_account", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="COGS Account"
              options={getFilteredAccountOptions("cogs_account", formData.cogs_account)}
              defaultValue={formData.cogs_account}
              onChange={(e) => handleFieldChange("cogs_account", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="GRNI Account"
              options={getFilteredAccountOptions("grni_account", formData.grni_account)}
              defaultValue={formData.grni_account}
              onChange={(e) => handleFieldChange("grni_account", e.target.value)}
            />
          </div>
        </div>

        {/* RECEIVABLES & PAYABLES */}
        <div className="accounting-card">
          <div className="accounting-card-title">RECEIVABLES &amp; PAYABLES</div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Default Accounts Receivable"
              options={getFilteredAccountOptions("default_accounts_receivable", formData.default_accounts_receivable)}
              defaultValue={formData.default_accounts_receivable}
              onChange={(e) => handleFieldChange("default_accounts_receivable", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Default Accounts Payable"
              options={getFilteredAccountOptions("default_accounts_payable", formData.default_accounts_payable)}
              defaultValue={formData.default_accounts_payable}
              onChange={(e) => handleFieldChange("default_accounts_payable", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Customer Advance Account"
              options={getFilteredAccountOptions("customer_advance_account", formData.customer_advance_account)}
              defaultValue={formData.customer_advance_account}
              onChange={(e) => handleFieldChange("customer_advance_account", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Vendor Advance Account"
              options={getFilteredAccountOptions("vendor_advance_account", formData.vendor_advance_account)}
              defaultValue={formData.vendor_advance_account}
              onChange={(e) => handleFieldChange("vendor_advance_account", e.target.value)}
            />
          </div>
        </div>

        {/* TAXES */}
        <div className="accounting-card">
          <div className="accounting-card-title">TAXES</div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Tax Payable Account"
              options={getFilteredAccountOptions("tax_payable_account", formData.tax_payable_account)}
              defaultValue={formData.tax_payable_account}
              onChange={(e) => handleFieldChange("tax_payable_account", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Tax Receivable Account"
              options={getFilteredAccountOptions("tax_receivable_account", formData.tax_receivable_account)}
              defaultValue={formData.tax_receivable_account}
              onChange={(e) => handleFieldChange("tax_receivable_account", e.target.value)}
            />
          </div>
        </div>

        {/* OTHERS */}
        <div className="accounting-card">
          <div className="accounting-card-title">OTHERS</div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Suspense Account"
              options={getFilteredAccountOptions("suspense_account", formData.suspense_account)}
              defaultValue={formData.suspense_account}
              onChange={(e) => handleFieldChange("suspense_account", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Retained Earnings Account"
              options={getFilteredAccountOptions("retained_earnings_account", formData.retained_earnings_account)}
              defaultValue={formData.retained_earnings_account}
              onChange={(e) => handleFieldChange("retained_earnings_account", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Rounding Account"
              options={getFilteredAccountOptions("rounding_account", formData.rounding_account)}
              defaultValue={formData.rounding_account}
              onChange={(e) => handleFieldChange("rounding_account", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Opening Balance Account"
              options={getFilteredAccountOptions("opening_balance_equity_account", formData.opening_balance_equity_account)}
              defaultValue={formData.opening_balance_equity_account}
              onChange={(e) => handleFieldChange("opening_balance_equity_account", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Discount Expense Account"
              options={getFilteredAccountOptions("discount_expense_account", formData.discount_expense_account)}
              defaultValue={formData.discount_expense_account}
              onChange={(e) => handleFieldChange("discount_expense_account", e.target.value)}
            />
          </div>
          <div className="accounting-field-group">
            <SearchSelectBoxDrown
              label="Discount Income Account"
              options={getFilteredAccountOptions("discount_income_account", formData.discount_income_account)}
              defaultValue={formData.discount_income_account}
              onChange={(e) => handleFieldChange("discount_income_account", e.target.value)}
            />
          </div>
        </div>

        <div style={{ marginTop: '24px', marginBottom: '40px', display: 'flex', justifyContent: 'flex-start' }}>
          <button
            type="button"
            className="accounting-btn-secondary"
            onClick={() => navigate('/accounting/configuration')}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="accounting-btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AccountingConfigurationEdit;
